import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { jwtDecode } from "jwt-decode";
import { authApiLogout } from "@/services/api";
import {
  clearGinjaLocalStorage,
  collectDepartmentPermissionKeys,
  consumeTokenFromHash,
  fetchAccessContext,
  mapStoredGinjaUserInfoToAppUser,
  persistAccessContext,
  readAccessContextFromStorage,
  readCookie,
  readGinjaUserIdFromStorage,
  redirectToParentLogin,
  resolveMergedBearer,
  ASSISTED_ONBOARDING_DEPT_KEY,
  GINJA_COOKIE_NAME,
  SSO_COOKIE_SENTINEL,
  type AppUser,
  type AoPermissionKey,
} from "@/services/ginjaAuth";
import {
  applyBrandSettings,
  applyCachedBrandSettings,
  fetchBrandSettings,
  resetBrandToDefaults,
} from "@/services/ginjaBrand";

type AuthContextValue = {
  user: AppUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  /** After legacy email/OTP login — syncs `hr-*` keys and React user. */
  setLegacySession: (token: string, hrResult: Record<string, unknown>) => void;
  /**
   * Ginja `/me/access` → `assisted_onboarding` permission keys.
   * `null` = not a Ginja-scoped session (legacy CAS) → treat as allow-all for checks.
   */
  aoPermissionSet: ReadonlySet<string> | null;
  hasAoPermission: (key: AoPermissionKey | string) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function sessionKeysValid(): boolean {
  const authTok = localStorage.getItem("auth-token");
  if (authTok) return true;
  const tok = localStorage.getItem("token");
  if (tok && tok !== SSO_COOKIE_SENTINEL) return true;
  const hr = localStorage.getItem("hr-auth-token");
  if (!hr) return false;
  try {
    const d = jwtDecode<{ exp?: number }>(hr);
    if (d.exp == null) return true;
    return Date.now() < d.exp * 1000;
  } catch {
    return false;
  }
}

function mapLegacyHrResultToAppUser(result: Record<string, unknown>): AppUser {
  const first = String(result.first_name ?? "");
  const last = String(result.last_name ?? "");
  const name =
    (typeof result.name === "string" && result.name.trim()) ||
    `${first} ${last}`.trim() ||
    "User";
  return {
    name,
    email: typeof result.email === "string" ? result.email : "",
    avatar: typeof result.avatar === "string" ? result.avatar : undefined,
  };
}

async function applyBrandAfterSession(): Promise<void> {
  const fresh = await fetchBrandSettings();
  if (fresh) applyBrandSettings(fresh);
  else applyCachedBrandSettings();
}

function computeAoPermissionSetFromStorage(): ReadonlySet<string> | null {
  if (!readGinjaUserIdFromStorage()) return null;
  const ctx = readAccessContextFromStorage();
  if (!ctx) return new Set();
  return collectDepartmentPermissionKeys(ctx, ASSISTED_ONBOARDING_DEPT_KEY);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [aoPermissionSet, setAoPermissionSet] = useState<
    ReadonlySet<string> | null
  >(null);

  const bootstrap = useCallback(async () => {
    setIsLoading(true);
    try {
      const hashToken = consumeTokenFromHash();
      const cookieReadable = readCookie(GINJA_COOKIE_NAME);
      const authTok = localStorage.getItem("auth-token");

      // Order matters: do not use orphan `token` for /me/access when only an HttpOnly SSO cookie exists —
      // a stale JWT in `localStorage.token` breaks cookie-only auth (`null` hint must stay null; see fetchAccessContext).
      let hintFromClient: string | null =
        hashToken ||
        cookieReadable ||
        (authTok && authTok !== SSO_COOKIE_SENTINEL ? authTok : null);
      if (!hintFromClient) {
        const t = localStorage.getItem("token");
        if (t && t !== SSO_COOKIE_SENTINEL && authTok) {
          hintFromClient = t;
        }
      }

      let access: Awaited<ReturnType<typeof fetchAccessContext>> = null;
      try {
        access = await fetchAccessContext(hintFromClient);
      } catch {
        access = null;
      }
      if (access) {
        const merged = resolveMergedBearer(access, hintFromClient);
        persistAccessContext(merged, access);
        setAoPermissionSet(
          collectDepartmentPermissionKeys(access, ASSISTED_ONBOARDING_DEPT_KEY),
        );
        setUser(mapStoredGinjaUserInfoToAppUser() ?? null);
        try {
          await applyBrandAfterSession();
        } catch {
          applyCachedBrandSettings();
        }
        return;
      }

      if (localStorage.getItem("auth-token")) {
        const restored = mapStoredGinjaUserInfoToAppUser();
        if (restored) {
          setAoPermissionSet(computeAoPermissionSetFromStorage());
          setUser(restored);
          try {
            await applyBrandAfterSession();
          } catch {
            applyCachedBrandSettings();
          }
          return;
        }
      }

      const hrTok = localStorage.getItem("hr-auth-token");
      const hrRaw = localStorage.getItem("hrUser");
      if (hrTok && hrRaw) {
        let expired = true;
        try {
          const d = jwtDecode<{ exp?: number }>(hrTok);
          expired = d.exp != null && Date.now() >= d.exp * 1000;
        } catch {
          expired = true;
        }
        if (!expired) {
          try {
            const parsed = JSON.parse(hrRaw) as Record<string, unknown>;
            setAoPermissionSet(null);
            setUser(mapLegacyHrResultToAppUser(parsed));
          } catch {
            setUser(null);
            setAoPermissionSet(null);
          }
          return;
        }
      }

      await authApiLogout();
      clearGinjaLocalStorage();
      localStorage.removeItem("hr-auth-token");
      localStorage.removeItem("hrUser");
      resetBrandToDefaults();
      setUser(null);
      setAoPermissionSet(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  const logout = useCallback(async () => {
    await authApiLogout();
    localStorage.removeItem("hr-auth-token");
    localStorage.removeItem("hrUser");
    clearGinjaLocalStorage();
    resetBrandToDefaults();
    setUser(null);
    setAoPermissionSet(null);
    redirectToParentLogin();
  }, []);

  const setLegacySession = useCallback((token: string, hrResult: Record<string, unknown>) => {
    localStorage.setItem("hr-auth-token", token);
    localStorage.setItem("hrUser", JSON.stringify(hrResult));
    setAoPermissionSet(null);
    setUser(mapLegacyHrResultToAppUser(hrResult));
  }, []);

  const isAuthenticated = Boolean(user && sessionKeysValid());

  const hasAoPermission = useCallback(
    (key: AoPermissionKey | string) =>
      aoPermissionSet === null || aoPermissionSet.has(key),
    [aoPermissionSet],
  );

  const value = useMemo(
    () => ({
      user,
      isLoading,
      isAuthenticated,
      logout,
      setLegacySession,
      aoPermissionSet,
      hasAoPermission,
    }),
    [
      user,
      isLoading,
      isAuthenticated,
      logout,
      setLegacySession,
      aoPermissionSet,
      hasAoPermission,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** Optional consumer when provider may be absent (tests). */